import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import SignatureBookModal from "./components/SignatureBookModal";
import { getExpiryStatus, getExpiryBadge } from "@/lib/signatureBookExpiry";
import ModuleGate from "@/components/base/ModuleGate";

interface SignatureBook {
  id: string;
  title: string;
  status: string;
  event_date: string | null;
  guest_book_enabled: boolean;
  cover_photo_url: string | null;
  public_slug: string | null;
  activated_at: string | null;
  expires_at: string | null;
  created_at: string;
  client?: { full_name: string } | null;
  _photo_count?: number;
  _entry_count?: number;
}

const STATUS_COLORS: Record<string, string> = {
  draft:     "bg-slate-100 text-slate-500",
  published: "bg-emerald-100 text-emerald-700",
  archived:  "bg-orange-100 text-orange-600",
};

const STATUS_LABELS: Record<string, string> = {
  draft:     "Borrador",
  published: "Publicado",
  archived:  "Archivado",
};

export default function AdminSignatureBooksPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]     = useState<string | null>(null);
  const [books, setBooks]           = useState<SignatureBook[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [showModal, setShowModal]   = useState(false);
  const [search, setSearch]         = useState("");

  const fetchBooks = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("signature_books")
        .select("*, activated_at, expires_at, client:client_id(full_name)")
        .eq("tenant_id", tid)
        .order("created_at", { ascending: false });
      if (err) throw new Error(err.message);

      const booksWithCounts = await Promise.all((data ?? []).map(async (b) => {
        const [photoRes, entryRes] = await Promise.all([
          supabase.from("signature_book_photos").select("id", { count: "exact", head: true }).eq("signature_book_id", b.id),
          supabase.from("signature_book_entries").select("id", { count: "exact", head: true }).eq("signature_book_id", b.id),
        ]);
        return { ...b, _photo_count: photoRes.count ?? 0, _entry_count: entryRes.count ?? 0 };
      }));

      setBooks(booksWithCounts as SignatureBook[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cargando libros");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireAuth();
      if (!ok) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchBooks(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchBooks]);

  const filtered = books.filter((b) =>
    b.title.toLowerCase().includes(search.toLowerCase()) ||
    (b.client?.full_name ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  const handleCreated = (id: string) => {
    setShowModal(false);
    navigate(`/admin/signature-books/${id}`);
  };

  return (
    <AdminLayout>
      <ModuleGate moduleId="signature_book" moduleName="Signature Book">
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 md:px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-slate-800 font-bold text-xl">Libros de Firmas</h1>
              <p className="text-slate-400 text-sm mt-0.5">Crea libros de firmas digitales con fotos y mensajes de invitados</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
              Nuevo Libro
            </button>
          </div>

          {/* Search */}
          <div className="mt-4 relative max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              placeholder="Buscar por título o cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-4 md:px-8 py-6">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchBooks(tenantId)} />
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-100 text-amber-500 mb-4">
                <i className="ri-book-open-line text-3xl"></i>
              </div>
              <h3 className="text-slate-700 font-semibold text-lg">
                {search ? "Sin resultados" : "Ningún libro creado aún"}
              </h3>
              <p className="text-slate-400 text-sm mt-2 max-w-xs">
                {search ? "Prueba con otra búsqueda." : "Crea tu primer libro de firmas para empezar."}
              </p>
              {!search && (
                <button
                  onClick={() => setShowModal(true)}
                  className="mt-5 flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i> Nuevo Libro
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((book) => (
                <div
                  key={book.id}
                  onClick={() => navigate(`/admin/signature-books/${book.id}`)}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden cursor-pointer hover:border-amber-300 transition-all group"
                >
                  {/* Cover */}
                  <div className="h-40 bg-gradient-to-br from-amber-50 to-orange-50 relative overflow-hidden">
                    {book.cover_photo_url ? (
                      <img
                        src={book.cover_photo_url}
                        alt={book.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-100 text-amber-400">
                          <i className="ri-book-open-line text-3xl"></i>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[book.status] ?? "bg-slate-100 text-slate-500"}`}>
                        {STATUS_LABELS[book.status] ?? book.status}
                      </span>
                      {(() => {
                        const expStatus = getExpiryStatus(book.expires_at ?? null);
                        const badge     = getExpiryBadge(expStatus);
                        if (!badge.label) return null;
                        return (
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${badge.className}`}>
                            <span className="w-3 h-3 flex items-center justify-center"><i className={`${badge.icon} text-[10px]`}></i></span>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                    {book.guest_book_enabled && (
                      <div className="absolute top-3 left-3">
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-500 text-white">
                          Visitas ON
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-4">
                    <h3 className="text-slate-800 font-semibold text-sm truncate">{book.title}</h3>
                    {book.client?.full_name && (
                      <p className="text-slate-400 text-xs mt-0.5 truncate flex items-center gap-1">
                        <i className="ri-user-3-line text-[10px]"></i>
                        {book.client.full_name}
                      </p>
                    )}
                    {book.event_date && (
                      <p className="text-slate-400 text-xs mt-0.5 flex items-center gap-1">
                        <i className="ri-calendar-line text-[10px]"></i>
                        {new Date(book.event_date).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-1 text-slate-500">
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-image-line text-xs"></i></span>
                        <span className="text-xs">{book._photo_count} fotos</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-500">
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-message-3-line text-xs"></i></span>
                        <span className="text-xs">{book._entry_count} mensajes</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showModal && tenantId && (
        <SignatureBookModal
          tenantId={tenantId}
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
        />
      )}
      </ModuleGate>
    </AdminLayout>
  );
}